import React, { useEffect, useRef, useState } from "react";

export default function VideoEdit() {
  const urls = [
    "https://firebasestorage.googleapis.com/v0/b/oing-portfolio.firebasestorage.app/o/videos%2Fc32c8cae-ea0e-4479-bea2-f51d5c27b604.mp4?alt=media&token=f6d4e16c-35d8-4c59-80e5-1f1a95ed42ad",
    "https://firebasestorage.googleapis.com/v0/b/oing-portfolio.firebasestorage.app/o/videos%2Fa61bf26b-f98d-4a9a-87dd-c0bab17106e0.mp4?alt=media&token=24062804-3a50-4882-91eb-1d9f11bd63ab",
  ];

  const [captions, setCaptions] = useState(["자막입니다.", "자막2입니다."]);
  const [transitionEffect, setTransitionEffect] = useState("fade");

  const mainRef1 = useRef(null);
  const mainRef2 = useRef(null);
  const previewRefs = [useRef(null), useRef(null)];

  const [useFirst, setUseFirst] = useState(true);
  const [durations, setDurations] = useState([0, 0]);
  const [trims, setTrims] = useState([
    { start: 2, end: 5, speed: 1.0 },
    { start: 2, end: 4, speed: 1.25 },
  ]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const effDur = trims.map((t) => (t.end - t.start) / t.speed);
  const totalDuration = effDur[0] + effDur[1];

  const curMain = useFirst ? mainRef1 : mainRef2;
  const nextMain = useFirst ? mainRef2 : mainRef1;

  const handleMainEnded = () => {
    if (useFirst) {
      setUseFirst(false);
      nextMain.current.currentTime = trims[1].start;
      nextMain.current.playbackRate = trims[1].speed;
      nextMain.current.play();
    } else {
      setIsPlaying(false);
    }
  };

  const toggleMainPlay = () => {
    if (isPlaying) {
      mainRef1.current.pause();
      mainRef2.current.pause();
    } else {
      const idx = useFirst ? 0 : 1;
      const toPlay = useFirst ? mainRef1.current : mainRef2.current;
      toPlay.currentTime = trims[idx].start;
      toPlay.playbackRate = trims[idx].speed;
      toPlay.play();
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const id = setInterval(() => {
      const v = curMain.current;
      if (!v) return;
      const idx = useFirst ? 0 : 1;

      const passed =
        (idx === 1 ? effDur[0] : 0) +
        (v.currentTime - trims[idx].start) / trims[idx].speed;
      setCurrentTime(Math.max(0, passed));

      if (
        v.currentTime >= trims[idx].end - 0.4 &&
        nextMain.current &&
        !nextMain.current.currentTime
      ) {
        nextMain.current.currentTime = trims[1].start;
        nextMain.current.playbackRate = trims[1].speed;
        nextMain.current.play().then(() => nextMain.current.pause());
      }

      if (v.currentTime >= trims[idx].end) handleMainEnded();
    }, 100);
    return () => clearInterval(id);
  }, [useFirst, trims, effDur]);

  const handleLoadedMeta = (idx) => {
    const vid = idx === 0 ? mainRef1.current : mainRef2.current;
    if (vid) {
      setDurations((p) => {
        const c = [...p];
        c[idx] = vid.duration || 0;
        return c;
      });
    }
  };

  const updateTrim = (idx, field, val) => {
    setTrims((p) => {
      const c = [...p];
      c[idx] = { ...c[idx], [field]: parseFloat(val) || 0 };
      if (c[idx].end < c[idx].start) c[idx].end = c[idx].start;
      return c;
    });
  };

  const previewPlay = (idx) => {
    const v = previewRefs[idx].current;
    if (!v) return;
    v.currentTime = trims[idx].start;
    v.playbackRate = trims[idx].speed;
    v.play();
  };
  const previewTimeUpdate = (idx) => {
    const v = previewRefs[idx].current;
    if (v && v.currentTime >= trims[idx].end) v.pause();
  };

  const getTransitionClass = (isActive, isFirst) => {
    if (transitionEffect === "none") {
      return `absolute top-0 left-0 w-full h-full ${isActive ? "z-10" : "z-0"}`;
    }

    if (transitionEffect === "fade") {
      return `absolute top-0 left-0 w-full h-full transition-opacity duration-700 ease-in-out ${
        isActive ? "opacity-100 z-10" : "opacity-0 z-0"
      }`;
    }

    if (transitionEffect === "slide-left") {
      return `absolute top-0 left-0 w-full h-full transition-transform duration-700 ease-in-out ${
        isActive
          ? "translate-x-0 z-10"
          : isFirst
          ? "-translate-x-full z-0"
          : "translate-x-full z-0"
      }`;
    }

    if (transitionEffect === "slide-right") {
      return `absolute top-0 left-0 w-full h-full transition-transform duration-700 ease-in-out ${
        isActive
          ? "translate-x-0 z-10"
          : isFirst
          ? "translate-x-full z-0"
          : "-translate-x-full z-0"
      }`;
    }

    return "";
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <h1 className="text-xl font-bold">🎬 전환 효과 + 자막 + 트리밍</h1>

      {/* 전환 효과 선택 */}
      <div className="flex items-center gap-3 text-sm">
        <label className="font-medium">전환 효과:</label>
        <select
          value={transitionEffect}
          onChange={(e) => setTransitionEffect(e.target.value)}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="none">효과 없음 (none)</option>
          <option value="fade">페이드 (fade)</option>
          <option value="slide-left">슬라이드 왼쪽 (slide-left)</option>
          <option value="slide-right">슬라이드 오른쪽 (slide-right)</option>
        </select>
      </div>

      {/* 메인 비디오 */}
      <div className="relative overflow-hidden">
        <video
          ref={mainRef1}
          src={urls[0]}
          onLoadedMetadata={() => handleLoadedMeta(0)}
          className={getTransitionClass(useFirst, true)}
        />
        <video
          ref={mainRef2}
          src={urls[1]}
          onLoadedMetadata={() => handleLoadedMeta(1)}
          className={getTransitionClass(!useFirst, false)}
        />
        <div className="absolute bottom-8 w-full text-center z-20 pointer-events-none">
          <span className="bg-black bg-opacity-60 text-white text-sm px-3 py-1 rounded">
            {captions[useFirst ? 0 : 1]}
          </span>
        </div>
        <div className="pt-[56.25%]" />
      </div>

      {/* 상태 표시 & 컨트롤 */}
      <div className="flex justify-between text-sm text-gray-700">
        <span>현재: {currentTime.toFixed(2)}s</span>
        <span>총 길이: {totalDuration.toFixed(2)}s</span>
      </div>
      <button
        onClick={toggleMainPlay}
        className="px-4 py-2 bg-blue-600 text-white rounded"
      >
        {isPlaying ? "⏸ 일시정지" : "▶ 재생"}
      </button>

      {/* 영상별 설정 */}
      <div className="grid md:grid-cols-2 gap-6">
        {urls.map((u, i) => (
          <div key={i} className="border p-4 rounded space-y-2">
            <h2 className="font-semibold text-sm">영상 {i + 1}</h2>
            <video
              ref={(el) => (previewRefs[i].current = el)}
              src={u}
              controls
              onTimeUpdate={() => previewTimeUpdate(i)}
              className="w-full rounded"
            />
            <input
              type="text"
              value={captions[i]}
              onChange={(e) => {
                const copy = [...captions];
                copy[i] = e.target.value;
                setCaptions(copy);
              }}
              placeholder="자막 입력"
              className="w-full border p-1 text-xs"
            />
            <div className="flex items-center gap-2 text-xs">
              <label className="flex gap-1 items-center">
                시작
                <input
                  type="number"
                  min="0"
                  value={trims[i].start}
                  onChange={(e) => updateTrim(i, "start", e.target.value)}
                  className="border p-1 w-16"
                />
              </label>
              <label className="flex gap-1 items-center">
                종료
                <input
                  type="number"
                  min={trims[i].start}
                  value={trims[i].end}
                  onChange={(e) => updateTrim(i, "end", e.target.value)}
                  className="border p-1 w-16"
                />
              </label>
              <label className="flex gap-1 items-center">
                속도
                <input
                  type="number"
                  step="0.1"
                  value={trims[i].speed}
                  onChange={(e) => updateTrim(i, "speed", e.target.value)}
                  className="border p-1 w-16"
                />
              </label>
              <button
                onClick={() => previewPlay(i)}
                className="ml-auto px-2 py-1 bg-green-600 text-white rounded"
              >
                ▶ 미리보기
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
